import {objectFromEntries} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {DataFrameColumnToFieldMapping} from 'analysis-tool/structured-data-mapping/DataMappingTypes';
import ColumnDropdownSelector from 'data-frames/column-selector/ColumnDropdownSelector';
import {DataFrameSpec} from 'data-frames/DataFrameTypes';
import {ResultSchema} from 'results/assay-schemas/AssaySchemaConstants';
import {FONT_PROXIMA_13} from 'styles/FontConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import {GRAY_DARK} from 'ui-library/Colors';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import getFieldIcon from './getFieldIcon';

export interface ColumnMappingTableProps {
  inputDataFrameSpec: DataFrameSpec;
  outputResultSchema: ResultSchema;
  mappings: Array<DataFrameColumnToFieldMapping>;
  setMappings: (newMappings: Array<DataFrameColumnToFieldMapping>) => void;
  errors?: Array<{outputFieldId: string; message: string}>;
}

function updateMappingsOnSelect(
  mappings: Array<DataFrameColumnToFieldMapping>,
  inputColumnSystemName: string | null,
  outputFieldId: string
): Array<DataFrameColumnToFieldMapping> {
  // Remove old selection for `outputFieldId`
  const newMappings = mappings.filter((m) => m.outputFieldId !== outputFieldId);
  if (inputColumnSystemName != null) {
    // If an actual `inputColumnSystemName` is selected, then add new selection for `outputFieldId`
    newMappings.push({inputColumnSystemName, outputFieldId});
  }
  return newMappings;
}

/**
 * Table that allows the user to configure the mapping from (input) Data Frame columns to (output) Result
 * Schema fields
 *
 * Following the designs, the table is output-centric: the rows correspond to the output fields, in order.
 * Each row starts with the output field and then renders an input column dropdown selector for that field.
 * ("For this output Result Schema field, which input Data Frame column should map to it?") For example, if
 * the input Data Frame has 5 columns and the Result Schema has 3 fields, the table renders 3 rows, each with
 * a dropdown selector with 5 options.
 *
 * Internally, the input columns (1) are identified with their `systemName` and (2) are rendered with their
 * `displayName`. The output fields (1) are identified with their `apiId` and (2) are rendered with their
 * `displayName`.
 *
 * TODO(BNCH-51640): Polish UI.
 */
export default function ColumnMappingTable(props: ColumnMappingTableProps): JSX.Element {
  const outputFields = props.outputResultSchema.fields;
  const outputFieldIdToSelectedInputColumnSystemName = objectFromEntries(
    props.mappings.map((m) => [m.outputFieldId, m.inputColumnSystemName])
  );
  return (
    <table className="table" {...getTestProps({component: 'ColumnMappingTable', element: 'root'})}>
      <thead>
        <tr>
          <th>Result column</th>
          <th>Analysis table column</th>
        </tr>
      </thead>
      <tbody>
        {outputFields.length > 0 ? (
          outputFields.map((outputField) => (
            <tr key={outputField.apiId}>
              <td className={css(styles.textColumn)}>
                <BenchlingIcon className={css(styles.typeIcon)} icon={getFieldIcon(outputField)} />
                {outputField.displayName}
              </td>
              <td className={css(styles.dropdownSelectorColumn)}>
                <ColumnDropdownSelector
                  dataFrameSpec={props.inputDataFrameSpec}
                  selectedColumnSystemName={
                    outputFieldIdToSelectedInputColumnSystemName[outputField.apiId] ?? null
                  }
                  onChange={(newColumnSystemName): void => {
                    props.setMappings(
                      updateMappingsOnSelect(props.mappings, newColumnSystemName, outputField.apiId)
                    );
                  }}
                  error={props.errors?.find((error) => error.outputFieldId === outputField.apiId)?.message}
                  dataTestAttrs={{
                    component: 'ColumnMappingTable',
                    element: 'ColumnDropdownSelector',
                  }}
                />
              </td>
            </tr>
          ))
        ) : (
          <tr>
            <td colSpan={2}>This schema does not have any fields</td>
          </tr>
        )}
      </tbody>
    </table>
  );
}

const styles = StyleSheet.create({
  dropdownSelectorColumn: {
    // So that the width is fixed and thus does not change as the user selects different options
    width: 255,
  },
  textColumn: {
    ...FONT_PROXIMA_13,
    // So that the shorter text aligns with the taller dropdown selector
    verticalAlign: 'middle',
  },
  typeIcon: {
    color: GRAY_DARK,
    marginRight: 8,
  },
});
